import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { clearToken } from "@/lib/auth";
import {
  CalendarCheck, Images, Sparkles, Tag, Users, LogOut, ExternalLink, Menu, X, Heart, Film, Wand2, Rocket, CalendarDays, Lightbulb, Coffee, BarChart3, LayoutGrid
} from "lucide-react";
import AppointmentsPanel from "@/components/admin/AppointmentsPanel";
import GalleryPanel from "@/components/admin/GalleryPanel";
import SocialPanel from "@/components/admin/SocialPanel";
import OffersPanel from "@/components/admin/OffersPanel";
import PatientsPanel from "@/components/admin/PatientsPanel";
import QuizPanel from "@/components/admin/QuizPanel";
import ReelsPanel from "@/components/admin/ReelsPanel";
import TemplatesPanel from "@/components/admin/TemplatesPanel";
import LaunchPanel from "@/components/admin/LaunchPanel";
import MonthlyPanel from "@/components/admin/MonthlyPanel";
import InspirationPanel from "@/components/admin/InspirationPanel";
import LifestylePanel from "@/components/admin/LifestylePanel";
import InsightsPanel from "@/components/admin/InsightsPanel";
import FeedPreviewPanel from "@/components/admin/FeedPreviewPanel";

const NAV_SECTIONS = [
  {
    id: "clinic",
    label: "Operación clínica",
    items: [
      { id: "appointments", label: "Citas", icon: CalendarCheck },
      { id: "patients", label: "Pacientes", icon: Users },
      { id: "gallery", label: "Galería", icon: Images },
    ],
  },
  {
    id: "content",
    label: "Marketing & contenido",
    items: [
      { id: "monthly", label: "Programación mensual", icon: CalendarDays },
      { id: "feedpreview", label: "Vista previa feed", icon: LayoutGrid },
      { id: "launch", label: "Lanzamiento (30 días)", icon: Rocket },
      { id: "inspiration", label: "Inspiración / Recrear", icon: Lightbulb },
      { id: "lifestyle", label: "Lifestyle personal", icon: Coffee },
      { id: "reels", label: "Reels Studio", icon: Film },
      { id: "templates", label: "Plantillas IG", icon: Wand2 },
      { id: "social", label: "Posts sueltos & IA", icon: Sparkles },
      { id: "insights", label: "Insights & performance", icon: BarChart3 },
    ],
  },
  {
    id: "conversion",
    label: "Captación & conversión",
    items: [
      { id: "offers", label: "Ofertas WhatsApp", icon: Tag },
      { id: "quiz", label: "Quiz de belleza", icon: Heart },
    ],
  },
];

const NAV = NAV_SECTIONS.flatMap((s) => s.items);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [active, setActive] = useState("appointments");
  const [openMobile, setOpenMobile] = useState(false);

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 top-0 left-0 h-full w-72 bg-[#1A1C1A] text-[#D9CDBC] flex flex-col transition-transform ${openMobile ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="admin-sidebar">
        <div className="px-7 py-8 border-b border-[#2a2b29] flex items-center gap-3">
          <div className="w-11 h-11 rounded-full border border-[#A69076] flex items-center justify-center flex-shrink-0">
            <span className="font-display text-lg text-[#A69076]">tm</span>
          </div>
          <div>
            <div className="font-display text-xl text-[#FAFAFA] leading-tight">Dra. Tania Mendoza</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A69076] mt-1">Panel Privado</div>
          </div>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-5 overflow-y-auto">
          {NAV_SECTIONS.map((section) => (
            <div key={section.id}>
              <div className="px-4 mb-2 text-[9px] tracking-[0.28em] uppercase text-[#A69076]/70 font-medium">
                {section.label}
              </div>
              <div className="space-y-1">
                {section.items.map((n) => {
                  const Icon = n.icon;
                  const isActive = active === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() => { setActive(n.id); setOpenMobile(false); }}
                      data-testid={`nav-${n.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${isActive ? "bg-[#A69076] text-[#1A1C1A]" : "hover:bg-[#2a2b29]"}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" /> {n.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 space-y-1 border-t border-[#2a2b29]">
          <Link to="/" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm hover:bg-[#2a2b29]" data-testid="open-public-site">
            <ExternalLink className="w-4 h-4" /> Ver sitio público
          </Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm hover:bg-[#2a2b29] text-[#D9CDBC]" data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pl-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E6E2DC]">
          <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2" onClick={() => setOpenMobile(!openMobile)} data-testid="mobile-menu-toggle">
                {openMobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div>
                <div className="font-display text-xl text-[#1A1C1A]">
                  {NAV.find((n) => n.id === active)?.label}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#5C5E5C]">Gestión interna</div>
              </div>
            </div>
          </div>
        </header>
        <div className="p-6 lg:p-10">
          {active === "appointments" && <AppointmentsPanel />}
          {active === "launch" && <LaunchPanel />}
          {active === "inspiration" && <InspirationPanel />}
          {active === "lifestyle" && <LifestylePanel />}
          {active === "monthly" && <MonthlyPanel />}
          {active === "feedpreview" && <FeedPreviewPanel />}
          {active === "quiz" && <QuizPanel />}
          {active === "templates" && <TemplatesPanel />}
          {active === "reels" && <ReelsPanel />}
          {active === "gallery" && <GalleryPanel />}
          {active === "social" && <SocialPanel />}
          {active === "insights" && <InsightsPanel />}
          {active === "offers" && <OffersPanel />}
          {active === "patients" && <PatientsPanel />}
        </div>
      </main>
    </div>
  );
}
